import {
  ProposalCardStatusEnum,
  ProposalCardTypeEnum
} from "@repo/core/enums/proposals";
import { WayupProposalStatusInternal } from "@repo/core/constants/proposals";
import {
  ProposalGroupedByDateItem,
  ConsultProposalResponseDTO,
  ProposalAnalysisItemDTO,
  ProposalDocumentDTO,
  ConsultProposalSituationResponseDTO
} from "@repo/core/services/entities/Proposals";
import { ProposalAnalysisResultDTO } from "@repo/core/services/entities/WayupRequisitions";
import { ProposalCardProps } from "@repo/core/components/layout/ProposalCard/types";
import { formatDateWithCurrentDay } from "@repo/core/utils/date.utils";
import { Proposals } from "@repo/core/components/layout/ProposalGroup/types";
import { ConsultProposalsGroupedByDateResponseDTO } from "@repo/core/services/entities/Proposals";
import { ProposalAnalyticsProps } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/sections/ProposalDetails/types";
import { ProposalDocument } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/sections/DocumentsView/types";
import { IconNames } from "@repo/core/components/core/Icon/types";

/**
 * Maps proposal status code to ProposalCardStatusEnum
 * Normalizes status code to always have 2 digits with leading zero if needed
 */
const mapStatusToEnum = (statusCode: string): ProposalCardStatusEnum => {
  // Normalize status code: ensure it has 2 digits with leading zero if needed
  // e.g., "4" -> "04", "04" -> "04", "20" -> "20"
  const normalizedStatusCode =
    statusCode.length === 1 ? `0${statusCode}` : statusCode;

  return (
    WayupProposalStatusInternal[
      normalizedStatusCode as keyof typeof WayupProposalStatusInternal
    ] || ProposalCardStatusEnum.IN_PROGRESS
  );
};

/**
 * Maps product code to ProposalCardTypeEnum
 * Based on product code, determines if it's FID or FID_CREDIT
 */
const mapProductToType = (typeProposal: string): ProposalCardTypeEnum => {
  switch (typeProposal) {
    case "A":
      return ProposalCardTypeEnum.FID_CREDIT;
    case "F":
      return ProposalCardTypeEnum.FID;
    default:
      return ProposalCardTypeEnum.NONE;
  }
};

/**
 * Maps API proposal data to ProposalCard format
 */
export const mapProposalToCard = (
  proposal: ProposalGroupedByDateItem
): ProposalCardProps => {
  return {
    proposalId: proposal.lngProposta.toString(),
    name: proposal.strNome,
    status: mapStatusToEnum(proposal.strSituacao),
    statusCode: String(proposal.strSituacao ?? ""),
    statusDescription: proposal.strSituacaoDescricao,
    type: mapProductToType(proposal.strTipoProposta),
    proposalTypeCode: proposal.strTipoProposta,
    cardName: proposal.strProdutoDescricao
  };
};

/**
 * Converts date string from API format (dd/MM/yyyy) to formatted date with day name
 */
const formatProposalDate = (dateString: string): string => {
  const [day, month, year] = dateString.split("/");
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return formatDateWithCurrentDay(date);
};

/**
 * Maps API proposals grouped by date to ProposalGroup format
 * Returns all proposals grouped by date
 */
export const mapProposalsGroupedByDate = (
  proposalsData: ConsultProposalsGroupedByDateResponseDTO
): Proposals[] => {
  if (!proposalsData || !Array.isArray(proposalsData)) {
    return [];
  }

  return proposalsData.map((group) => {
    const orderedProposalsByNumber = [...group.lstPropostas].sort(
      (a, b) => b.lngProposta - a.lngProposta
    );
    return {
      date: formatProposalDate(group.datDataSituacao),
      items: orderedProposalsByNumber.map((proposal) =>
        mapProposalToCard(proposal)
      )
    };
  });
};

/**
 * Maps API proposals grouped by date to ProposalCard format
 * Filters by today's date and returns only the first N proposals
 */
export const mapRecentProposals = (
  proposalsData: ConsultProposalsGroupedByDateResponseDTO,
  limit?: number
): ProposalCardProps[] => {
  if (!proposalsData || !Array.isArray(proposalsData)) {
    return [];
  }

  const today = new Date();
  const todayFormatted = `${String(today.getDate()).padStart(2, "0")}/${String(
    today.getMonth() + 1
  ).padStart(2, "0")}/${today.getFullYear()}`;

  const todayProposals = proposalsData.find(
    (group) => group.datDataSituacao === todayFormatted
  );

  if (
    !todayProposals?.lstPropostas ||
    todayProposals.lstPropostas.length === 0
  ) {
    return [];
  }

  const proposals = todayProposals.lstPropostas.map((proposal) =>
    mapProposalToCard(proposal)
  );

  return limit ? proposals.slice(0, limit) : proposals;
};

/**
 * Maps API proposals grouped by date to ProposalGroup format
 * Filters only proposals with status "20" (Draft status)
 */
export const mapDraftProposals = (
  proposalsData: ConsultProposalsGroupedByDateResponseDTO
): Proposals[] => {
  if (!proposalsData || !Array.isArray(proposalsData)) {
    return [];
  }

  return proposalsData
    .map((group) => {
      const draftProposals = group.lstPropostas.filter(
        (proposal) => proposal.strSituacao === "20"
      );

      if (draftProposals.length === 0) {
        return null;
      }

      return {
        date: formatProposalDate(group.datDataSituacao),
        items: draftProposals.map((proposal) => mapProposalToCard(proposal))
      };
    })
    .filter((group): group is Proposals => group !== null);
};

/**
 * Maps analysis item codes to human-readable names
 */
const mapAnalysisItemName = (itemCode: string): string => {
  const itemNameMap: Record<string, string> = {
    BIOMETRIAFACIALUNICO: "Biometria Facial",
    CREDITSCORE: "SCORE",
    NOMECLIENTE: "Validação Nome",
    OBITO: "Validação Sem Registro Óbito",
    PH3: "Inteligência Wayup",
    REGISTROSINTERNOS: "Dados Cadastrais",
    SCORESPC: "Score Reclassificação",
    SPC: "Número de Consultas",
    TELEFONES: "Validação Telefones",
    DATANASCIMENTO: "Validação Data de Nascimento",
    OCUPACAOPROFISSIONAL: "Ocupação Profissional",
    IDADE: "Idade",
    PROFISSAO: "Profissão",
    RENDA: "Renda",
    REGRADEANALISE: "Regra de Análise"
  };

  return itemNameMap[itemCode.toUpperCase()] || itemCode;
};

/**
 * Maps analysis item situation code to variant for CardCollapse
 * Only supports "success" and "error" variants
 */
const mapAnalysisSituationToVariant = (situacao: string): string => {
  switch (situacao.toUpperCase()) {
    case "A": // Approved
      return "success";
    case "R": // Rejected
      return "error";
    case "P": // Pending - treat as success for now
    default:
      return "success";
  }
};

/**
 * Maps analysis item situation to display text
 */
const mapAnalysisSituationToText = (situacao: string): string => {
  switch (situacao.toUpperCase()) {
    case "A": // Approved
      return "Aprovado.";
    case "R": // Rejected
      return "Reprovado.";
    case "P": // Pending
      return "Pendente.";
    default:
      return "";
  }
};

/**
 * Maps API analysis items to ProposalAnalyticsProps format
 */
export const mapProposalAnalysisItems = (
  analysisItems: ProposalAnalysisItemDTO[]
): ProposalAnalyticsProps[] => {
  if (!analysisItems || !Array.isArray(analysisItems)) {
    return [];
  }

  return analysisItems.map((item) => ({
    title: mapAnalysisItemName(item.item),
    subtitle: item.parecer || mapAnalysisSituationToText(item.situacao),
    isLoading: false,
    variant: mapAnalysisSituationToVariant(item.situacao),
    situacao: item.situacao
  }));
};

/**
 * Collects unique standard rejection reasons (motivorecusapadrao) for rejected analysis items.
 */
const ANALYSIS_RESULT_EXCLUDED_ITEMS = new Set(["FLUXOANALISE"]);

export const collectUniqueStandardRejectionReasons = (
  analysisReasons: ProposalAnalysisResultDTO[]
): string[] => {
  if (!analysisReasons?.length) {
    return [];
  }
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of analysisReasons) {
    if (ANALYSIS_RESULT_EXCLUDED_ITEMS.has(item.item)) {
      continue;
    }
    if (item.situacao?.toUpperCase() !== "R") {
      continue;
    }
    const reason = item.motivorecusapadrao?.trim();
    if (!reason) {
      continue;
    }
    const key = reason.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(reason);
  }
  return result;
};

/**
 * Maps analysis results from Wayup API to ProposalAnalyticsProps format
 */
export const mapAnalysisReasons = (
  analysisReasons: ProposalAnalysisResultDTO[]
): ProposalAnalyticsProps[] => {
  if (!analysisReasons || !Array.isArray(analysisReasons)) {
    return [];
  }

  return analysisReasons
    .filter((item) => !ANALYSIS_RESULT_EXCLUDED_ITEMS.has(item.item))
    .map((item) => ({
      title: item.label,
      subtitle:
        item.parecer?.trim() || mapAnalysisSituationToText(item.situacao),
      isLoading: false,
      variant: mapAnalysisSituationToVariant(item.situacao),
      situacao: item.situacao
    }));
};

/**
 * Formats CPF string to display format (xxx.xxx.xxx-xx)
 */
const formatCPF = (cpf: string): string => {
  if (!cpf) return "";
  // Remove all non-numeric characters
  const cleaned = cpf.replace(/\D/g, "");
  // Format as xxx.xxx.xxx-xx
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  return cpf;
};

/**
 * Formats date string from API format (dd/MM/yyyy) to display format
 */
const formatProposalDateDisplay = (dateString: string): string => {
  if (!dateString) return "";
  // If already in dd/MM/yyyy format, return as is
  if (dateString.includes("/")) {
    return dateString;
  }
  // If in ISO format (yyyy-MM-dd), convert to dd/MM/yyyy
  if (dateString.includes("-")) {
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  }
  return dateString;
};

/**
 * Interface for proposal details data
 */
export interface ProposalDetailsData {
  proposalNumber: string;
  clientName: string;
  cpf: string;
  proposalDate: string;
  productCode: string;
  productDescription: string;
  pointOfService: string;
  status: ProposalCardStatusEnum;
  statusCode?: string;
  statusDescription?: string;
  proposalTypeCode?: string;
  limits: {
    purchases: number;
    withdrawals: number;
    installment: number;
    monthlyInstallment: number;
  };
  analysisItems: ProposalAnalyticsProps[];
  /** Unique motivorecusapadrao values for rejected items (new analysis endpoint only) */
  standardRejectionReasons: string[];
}

/**
 * Maps API proposal data to ProposalDetailsData format
 * Now uses only getProposal endpoint data
 */
export const mapProposalDetails = (
  proposalData?: ConsultProposalResponseDTO,
  proposalSituationData?: ConsultProposalSituationResponseDTO
): ProposalDetailsData => {
  return {
    proposalNumber: proposalData?.lngProposta.toString() || "",
    clientName: proposalData?.strNome || "",
    cpf: formatCPF(proposalData?.strCpf || ""),
    proposalDate: formatProposalDateDisplay(
      proposalData?.datDataProposta || ""
    ),
    productCode: proposalData?.strProduto || "",
    productDescription: proposalData?.strProdutoDescricao || "",
    pointOfService:
      proposalData?.strPontoAntendimentoNome ||
      proposalData?.strPontoAtendimentoDescricao ||
      "",
    status: mapStatusToEnum(proposalData?.strSituacao || ""),
    statusCode: proposalData?.strSituacao,
    statusDescription: proposalData?.strSituacaoDescricao,
    proposalTypeCode: proposalData?.strTipoProposta,
    limits: {
      purchases: proposalSituationData?.dblLimiteCompras || 0,
      withdrawals: proposalSituationData?.dblLimiteSaques || 0,
      installment: proposalSituationData?.dblLimiteParcelado || 0,
      monthlyInstallment: proposalSituationData?.dblLimiteParceladoMensal || 0
    },
    analysisItems: mapProposalAnalysisItems(
      proposalData?.lstItensAnalise || []
    ),
    standardRejectionReasons: []
  };
};

/**
 * Maps document type code to icon name
 */
const mapDocumentTypeToIcon = (tipoDocumento: string): IconNames => {
  const tipo = tipoDocumento.trim();

  // Map document type codes to icons
  switch (tipo) {
    case "0001": // DOCUMENTO DE IDENTIFICAÇÃO FRENTE
    case "0002": // DOCUMENTO DE IDENTIFICAÇÃO VERSO
      return "id-card";
    case "0003": // BIOMETRIA FACIAL
      return "image-portrait";
    case "0004": // COMPROVANTE DE RENDA
      return "file-invoice-dollar";
    default:
      return "file";
  }
};

/**
 * Maps document type code to category type
 */
const mapDocumentTypeToCategory = (tipoDocumento: string): string => {
  const tipo = tipoDocumento.trim();

  switch (tipo) {
    case "0001":
    case "0002":
      return "Documentação";
    case "0003":
      return "Biometria";
    case "0004":
      return "Documentação";
    default:
      return "Documentação";
  }
};

/**
 * Formats document date and time for display
 */
const formatDocumentDateTime = (
  dataDocumento: string,
  horaDocumento: string
): string => {
  if (!dataDocumento) return "";

  // Data já vem no formato dd/MM/yyyy
  const horaFormatada = horaDocumento?.trim() || "";

  if (horaFormatada) {
    return `${dataDocumento} às ${horaFormatada}`;
  }

  return dataDocumento;
};

/**
 * Maps API document data to ProposalDocument format
 */
export const mapProposalDocument = (
  document: ProposalDocumentDTO
): ProposalDocument => {
  return {
    type: mapDocumentTypeToCategory(document.strTipoDocumento),
    title: document.strDescrTipoDocumento || "Documento",
    icon: mapDocumentTypeToIcon(document.strTipoDocumento),
    date: formatDocumentDateTime(
      document.strDataDocumento,
      document.strHoraDocumento
    ),
    buttonLabel: "Ver documento",
    base64: document.strBase64,
    thumbnail: document.thumbnail,
    documentId: document.lngDocumento,
    documentType: document.strTipoDocumento,
    extension: document.strExtensao
  };
};

/**
 * Maps API documents list to ProposalDocument array
 */
export const mapProposalDocuments = (
  documents: ProposalDocumentDTO[]
): ProposalDocument[] => {
  if (!documents || !Array.isArray(documents)) {
    return [];
  }

  return documents.map((document) => mapProposalDocument(document));
};
